import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { cn } from '@rocco/utils/cn';
import { ChatBoxRecipients } from './ChatBoxRecipients';
import { ChatBoxSuggestionsList } from './ChatBoxSuggestionsList';
import { ChatBoxMessages } from './ChatBoxMessages';
import { ChatBoxInput } from './ChatBoxInput';
import type { ContactSuggestion, Conversation } from '../../types';
import { useChat, preselectedRecipientsAtom } from '../../hooks';
import { MAX_PARTICIPANTS } from '../../utils';
import { useContactSuggestions } from '../../hooks/use-contact-suggestions';
import { useConversation } from '../../hooks/use-conversation';
import { useCreateConversation } from '../../hooks/use-create-conversation';

/**
 * The suggestions list, messages, and input for the new message chat box.
 * In forward mode, allows selecting an existing conversation to forward to.
 * @see {@link ChatBox}
 */
export function ChatBoxNewMessage({
    className,
    isOpen,
}: {
    className?: string;
    /**
     * Whether the chat box is open.
     */
    isOpen: boolean;
}) {
    const [search, setSearch] = useState('');
    const [recipients, setRecipients] = useState<ContactSuggestion[]>([]);
    const [selectedConversation, setSelectedConversation] =
        useState<Conversation | null>(null);
    const [scrollDownCounter, setScrollDownCounter] = useState(0);
    const [preselectedRecipients, setPreselectedRecipients] = useAtom(
        preselectedRecipientsAtom
    );
    const searchInputRef = useRef<HTMLInputElement>();
    const {
        forwardedMessage,
        setPendingMessage,
        setForwardedMessage,
        convertNewMessageToConversation,
    } = useChat();
    const isForwardMode = !!forwardedMessage;
    const {
        suggestions,
        conversations,
        hasMore: hasMoreSuggestions,
        isLoading: isLoadingSuggestions,
        setSearch: setSearchSuggestions,
        loadMore,
    } = useContactSuggestions({ limit: 50, includeChats: isForwardMode });
    const {
        conversation: conversationPreview,
        createdConversation,
        optimisticMessage,
        isLoading: isConversationLoading,
        isSubmitting: isCreatingConversation,
        createConversation,
    } = useCreateConversation({
        participantIDs: recipients.map((r) => r.ID),
    });
    const {
        conversation: fullSelectedConversation,
        messages: selectedConversationMessages,
        isLoading: isSelectedConversationLoading,
        isSubmitting: isSelectedConversationSubmitting,
    } = useConversation({
        id: selectedConversation ? Number(selectedConversation.ID) : 0,
    });
    // Determine which conversation to show (recipient-based or selected conversation).
    const activeConversation = selectedConversation
        ? (fullSelectedConversation ?? selectedConversation)
        : conversationPreview;
    const messages = useMemo(() => {
        if (selectedConversation) {
            return fullSelectedConversation
                ? selectedConversationMessages
                : selectedConversation.Messages.nodes;
        }
        const previewMessages = conversationPreview?.Messages.nodes ?? [];
        if (optimisticMessage) {
            return [optimisticMessage, ...previewMessages];
        }
        return previewMessages;
    }, [
        selectedConversation,
        fullSelectedConversation,
        selectedConversationMessages,
        conversationPreview,
        optimisticMessage,
    ]);

    const handleInputRef = useCallback((element?: HTMLInputElement) => {
        searchInputRef.current = element;
    }, []);

    const handleSearchChange = useCallback(
        (search: string, excludeMemberIDs?: number[]) => {
            setSearch(search);
            setSearchSuggestions(search, excludeMemberIDs);
        },
        [setSearchSuggestions]
    );

    function handleRecipientsChange(recipients: ContactSuggestion[]) {
        setRecipients(recipients);
        handleSearchChange(
            search,
            recipients.map((r) => r.ID)
        );
    }

    function addRecipient(suggestion: ContactSuggestion) {
        setRecipients((prev) => {
            if (
                prev.some((r) => r.ID === suggestion.ID) ||
                prev.length >= MAX_PARTICIPANTS
            ) {
                return prev;
            }
            const newRecipients = [...prev, suggestion];
            handleSearchChange(
                '',
                newRecipients.map((r) => r.ID)
            );
            return newRecipients;
        });
        setSelectedConversation(null);
        searchInputRef.current?.focus();
    }

    function handleConversationSelect(conversation: Conversation) {
        setSelectedConversation(conversation);
        setRecipients([]);
        handleSearchChange('');
    }

    // Load preselected recipients when the chat box opens
    useEffect(() => {
        if (
            isOpen &&
            preselectedRecipients.length > 0 &&
            suggestions.length > 0
        ) {
            // Find the contact suggestions matching the preselected recipient IDs
            const preselected = suggestions.filter((suggestion) =>
                preselectedRecipients.includes(suggestion.ID)
            );

            if (preselected.length > 0) {
                setRecipients(preselected);
                handleSearchChange(
                    '',
                    preselected.map((r) => r.ID)
                );
                // Clear the preselected recipients after using them
                setPreselectedRecipients([]);
            }
        }
    }, [
        isOpen,
        preselectedRecipients,
        suggestions,
        setPreselectedRecipients,
        handleSearchChange,
    ]);

    // Convert this new message chat box to a normal conversation chat box when
    // a new conversation is created.
    useEffect(() => {
        if (createdConversation) {
            convertNewMessageToConversation(Number(createdConversation.ID));
        }
    }, [createdConversation, convertNewMessageToConversation]);

    return (
        <div className={cn('flex flex-col gap-[8px]', className)}>
            <ChatBoxRecipients
                search={search}
                recipients={recipients}
                conversation={selectedConversation ?? undefined}
                maxRecipients={MAX_PARTICIPANTS}
                isOpen={isOpen}
                isDisabled={isCreatingConversation}
                onSearchChange={handleSearchChange}
                onRecipientsChange={handleRecipientsChange}
                onConversationRemove={() => setSelectedConversation(null)}
                onInputRef={handleInputRef}
            />
            <div className="h-px bg-white"></div>
            {!selectedConversation &&
            (recipients.length === 0 ||
                (search && recipients.length < MAX_PARTICIPANTS)) ? (
                <ChatBoxSuggestionsList
                    className="mt-[8px] min-h-0 flex-1"
                    isLoading={isLoadingSuggestions}
                    suggestions={suggestions}
                    conversations={isForwardMode ? conversations : undefined}
                    hasMore={hasMoreSuggestions}
                    onSuggestionClick={addRecipient}
                    onConversationClick={handleConversationSelect}
                    onLoadMore={loadMore}
                />
            ) : (
                <>
                    <ChatBoxMessages
                        className="min-h-0 flex-1"
                        conversation={activeConversation ?? undefined}
                        messages={messages}
                        isOpen={isOpen}
                        scrollDownCounter={scrollDownCounter}
                        hideActions
                    />
                    <ChatBoxInput
                        isOpen={isOpen}
                        isDisabled={
                            selectedConversation
                                ? isSelectedConversationLoading
                                : isConversationLoading
                        }
                        isSubmitting={
                            selectedConversation
                                ? isSelectedConversationSubmitting
                                : isCreatingConversation ||
                                  isConversationLoading
                        }
                        autoFocus={false}
                        replyingTo={forwardedMessage}
                        replyMode="forward"
                        onCancelReply={() => {
                            setForwardedMessage(null);
                            setSelectedConversation(null);
                        }}
                        onSend={({ message, attachments, replyTo }) => {
                            if (selectedConversation) {
                                const conversationID = Number(
                                    selectedConversation.ID
                                );
                                setPendingMessage({
                                    conversationID,
                                    message,
                                    attachments,
                                    replyTo,
                                });
                                convertNewMessageToConversation(conversationID);
                            } else if (conversationPreview) {
                                const conversationID = Number(
                                    conversationPreview.ID
                                );
                                setPendingMessage({
                                    conversationID,
                                    message,
                                    attachments,
                                    replyTo,
                                });
                                convertNewMessageToConversation(conversationID);
                            } else {
                                // This new message chat box will be converted to
                                // a normal conversation chat box when this action
                                // is completed (see useEffect above).
                                createConversation(
                                    message,
                                    attachments,
                                    replyTo
                                );
                            }
                            // Clear forwarding state after sending.
                            setForwardedMessage(null);
                        }}
                        onAttachmentsChanged={() => {
                            setScrollDownCounter((state) => state + 1);
                        }}
                    />
                </>
            )}
        </div>
    );
}
